import React, { useState, useEffect } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import GoalIcon from '@mui/icons-material/Flag'
import ProjectIcon from '@mui/icons-material/Folder'
import TaskIcon from '@mui/icons-material/CheckCircle'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import moment from 'moment'
import { roadmapAPI } from './api/roadmapAPI'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const StyledCard = styled(Card)(({ theme, horizon }) => {
  // For Life Dreams (DREAMS), use white card
  if (horizon === 'DREAMS') {
    return {
      backgroundColor: 'white',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      minWidth: '180px',
      maxWidth: '280px',
      flex: '0 0 auto',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
      },
    }
  }
  
  // For other horizons, use gradient background (purple to orange) with background image
  return {
    backgroundImage: 'linear-gradient(135deg, rgba(156, 39, 176, 0.7) 0%, rgba(255, 152, 0, 0.7) 100%), url(/small.png)',
    backgroundSize: 'cover, cover',
    backgroundPosition: 'center, center',
    backgroundBlendMode: 'overlay, normal',
    position: 'relative',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    minWidth: '180px',
    maxWidth: '280px',
    flex: '0 0 auto',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 29, 0.5)',
      borderRadius: '12px',
      zIndex: 0,
    },
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      borderColor: 'rgba(255, 255, 255, 0.5)',
      backgroundImage: 'linear-gradient(135deg, rgba(156, 39, 176, 0.8) 0%, rgba(255, 152, 0, 0.8) 100%), url(/small.png)',
      '&::before': {
        backgroundColor: 'rgba(0, 0, 29, 0.5)',
      },
    },
  }
})

const DOMAIN_COLORS = {
  PROFESSIONAL: '#1976d2',
  FINANCE: '#2e7d32',
  HEALTH: '#d32f2f',
  PERSONAL: '#ed6c02',
  SOCIAL: '#9c27b0',
  HOME: '#0288d1',
  ADMIN: '#616161',
  TIME: '#7b1fa2',
  SPIRITUAL: '#c2185b',
  SPORTS: '#f57c00',
  LEISURE: '#00acc1',
  FOOTPRINT: '#388e3c',
  OTHER: '#757575',
}

// LAP-733 (GE022): label values resolved via t() at render time
const IDEA_TYPE_KEYS = {
  ACCOMPLISH: 'ideaTypeAccomplish',
  BECOME: 'ideaTypeBecome',
  PROGRESS: 'ideaTypeProgress',
  EXPERIENCE: 'ideaTypeExperience',
}

const STATUS_KEYS = {
  IDEA: 'statusIdea',
  PLANNED: 'statusPlanned',
  ENGAGED: 'statusEngaged',
  COMPLETED: 'statusCompleted',
  ABANDONED: 'statusAbandoned',
}

const STATUS_COLORS = {
  IDEA: 'rgba(255, 255, 255, 0.3)',
  PLANNED: '#1565c0',
  ENGAGED: '#4caf50',
  COMPLETED: '#9c27b0',
  ABANDONED: '#f44336',
}

const IdeaCard = ({ idea, index, onClick, horizon }) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const domainColor = DOMAIN_COLORS[idea.domain] || DOMAIN_COLORS.OTHER
  const [parentIdeaName, setParentIdeaName] = useState(null)

  useEffect(() => {
    const fetchParentIdea = async () => {
      const parentId = idea.parent_idea || idea.parentIdea
      if (parentId) {
        try {
          const parent = await roadmapAPI.getIdea(parentId)
          setParentIdeaName(parent.title)
        } catch (error) {
          console.error('Error fetching parent idea:', error)
          setParentIdeaName(null)
        }
      } else {
        setParentIdeaName(null)
      }
    }

    fetchParentIdea()
  }, [idea.parent_idea, idea.parentIdea])

  return (
    <Draggable draggableId={idea.id} index={index}>
      {(provided, snapshot) => (
        <StyledCard
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          horizon={horizon}
          onClick={() => onClick(idea)}
          sx={{
            opacity: snapshot.isDragging ? 0.5 : 1,
          }}
        >
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, position: 'relative', zIndex: 1 }}>
            <Typography
              variant="body1"
              sx={{
                color: horizon === 'DREAMS' ? '#333' : 'white',
                fontSize: '0.875rem',
                fontWeight: 500,
                mb: horizon !== 'DREAMS' ? 0.5 : 0,
                lineHeight: 1.4,
              }}
            >
              {idea.title}
            </Typography>
            
            {/* Show domain/category below title for non-DREAMS horizons (including YEAR_10) */}
            {horizon !== 'DREAMS' && (
              <Box sx={{ mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.75rem',
                    display: 'block',
                  }}
                >
                  {idea.domain?.replace('_', ' ') || t('roadmap.catOther')}
                </Typography>
                {idea.idea_type && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '0.7rem',
                      display: 'block',
                      mt: 0.25,
                    }}
                  >
                    {IDEA_TYPE_KEYS[idea.idea_type] ? t('roadmap.' + IDEA_TYPE_KEYS[idea.idea_type]) : idea.idea_type}
                  </Typography>
                )}
              </Box>
            )}

            {/* Parent Idea Display */}
            {(idea.parent_idea || idea.parentIdea) && (
              <Box sx={{ mt: horizon === 'DREAMS' ? 0.75 : 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <CallSplitIcon sx={{ fontSize: '14px', color: horizon === 'DREAMS' ? '#9c27b0' : '#9c27b0' }} />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: horizon === 'DREAMS' ? '#9c27b0' : 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.7rem',
                      fontStyle: 'italic',
                    }}
                  >
                    {t('roadmap.fragmentOf')}
                  </Typography>
                  {parentIdeaName && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: horizon === 'DREAMS' ? '#7b1fa2' : 'rgba(255, 255, 255, 0.9)',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={parentIdeaName}
                    >
                      {parentIdeaName}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Status Display */}
            {idea.status && (
              <Box sx={{ mt: horizon === 'DREAMS' ? 0.75 : 0.5 }}>
                <Chip
                  label={STATUS_KEYS[idea.status] ? t('roadmap.' + STATUS_KEYS[idea.status]) : idea.status}
                  size="small"
                  sx={{
                    backgroundColor: STATUS_COLORS[idea.status] || 'rgba(255, 255, 255, 0.2)',
                    color: horizon === 'DREAMS' ? '#333' : 'white',
                    fontWeight: 500,
                    height: '20px',
                    fontSize: '0.65rem',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to right, rgba(0, 0, 0, 0.3) 0%, transparent 100%)',
                      borderRadius: 'inherit',
                      pointerEvents: 'none',
                    },
                    '& .MuiChip-label': {
                      px: 0.75,
                      position: 'relative',
                      zIndex: 1,
                    },
                  }}
                />
              </Box>
            )}

            {/* Linked items count - only for non-DREAMS horizons */}
            {horizon !== 'DREAMS' && idea.links && idea.links.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                {(() => {
                  const goals = idea.links.filter(l => l.link_type === 'GOAL' || l.content_object_info?.type === 'goal')
                  const projects = idea.links.filter(l => l.link_type === 'PROJECT' || l.content_object_info?.type === 'project')
                  const tasks = idea.links.filter(l => l.link_type === 'TASK' || l.content_object_info?.type === 'task')
                  
                  return (
                    <>
                      {goals.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <GoalIcon sx={{ fontSize: '14px', color: '#ff9800' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.65rem' }}>
                            {goals.length} {goals.length === 1 ? t('roadmap.goal') : t('roadmap.goals')}
                          </Typography>
                        </Box>
                      )}
                      {projects.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <ProjectIcon sx={{ fontSize: '14px', color: '#2196f3' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.65rem' }}>
                            {projects.length} {projects.length === 1 ? t('roadmap.project') : t('roadmap.projects')}
                          </Typography>
                        </Box>
                      )}
                      {tasks.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <TaskIcon sx={{ fontSize: '14px', color: '#4caf50' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.65rem' }}>
                            {tasks.length} {tasks.length === 1 ? t('roadmap.task') : t('roadmap.tasks')}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )
                })()}
              </Box>
            )}

          </CardContent>
        </StyledCard>
      )}
    </Draggable>
  )
}

export default IdeaCard

